import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useSubscriptionAccess } from '@/composables/useSubscriptionAccess';
import { useFeatureAccess } from '@/composables/useFeatureAccess';
import type { WalkFlow, WalkStep } from './walkthrough';

// A step needs the plan feature of the step (or its page's requiresSubscription).
// - stepAvailable (guide card): lenient while the plan is loading, like the router, so a
//   step is never skipped by mistake.
// - flowListed (guide list): strict; a guide is listed once the plan is known and any of
//   its steps is included, so guides the school can't use never flash in.
export function useGuideAvailability() {
  const router = useRouter();
  const { canAccessFeature } = useSubscriptionAccess();
  const featureAccess = useFeatureAccess();
  const planReady = computed(() => !featureAccess.loading.value && !!featureAccess.lastFetched.value);

  const featureOf = (step: WalkStep): string | undefined | false => {
    if (step.feature) return step.feature;
    try {
      return router.resolve({ name: step.route }).meta.requiresSubscription as string | undefined;
    } catch {
      return false; // unknown page
    }
  };
  const stepAvailable = (step: WalkStep) => {
    const feature = featureOf(step);
    return feature !== false && (!feature || canAccessFeature(feature));
  };
  const flowListed = (flow: WalkFlow) =>
    flow.steps.some((step) => {
      const feature = featureOf(step);
      if (feature === false) return false;
      return !feature || (planReady.value && featureAccess.hasFeature(feature));
    });
  // The router fetches the plan on plan-gated pages; fetch it here too if nothing has yet.
  const ensurePlan = () => {
    if (!featureAccess.loading.value && !featureAccess.lastFetched.value) void featureAccess.fetchFeatures().catch(() => {});
  };
  return { stepAvailable, flowListed, planReady, ensurePlan };
}
